import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import type { UB04PayerRow } from './types';
import {
  errorKey,
  fieldError,
  fieldSeverity,
  worksheetFindingInputClass,
  type WorksheetErrorMap,
} from './validationErrors';
import { WorksheetFieldError } from './WorksheetFieldError';

const ROWS: Array<'A' | 'B' | 'C'> = ['A', 'B', 'C'];

const PATIENT_RELATIONSHIP_OPTIONS = [
  { value: '18', label: '18 — Self' },
  { value: '01', label: '01 — Spouse' },
  { value: '19', label: '19 — Child' },
  { value: 'G8', label: 'G8 — Other relationship' },
];

const emptyRow = (row: 'A' | 'B' | 'C'): UB04PayerRow => ({ row });

/** One FL 50-65 cell, outlined and captioned when it carries a finding. */
function PayerField({
  label,
  locator,
  row,
  value,
  errors,
  disabled,
  onChange,
}: {
  label: string;
  locator: string;
  row: 'A' | 'B' | 'C';
  value?: string | null;
  errors?: WorksheetErrorMap;
  disabled: boolean;
  onChange: (next: string) => void;
}) {
  const error = errors ? fieldError(errors, locator, row) : undefined;
  const severity = errors ? fieldSeverity(errors, locator, row) : undefined;
  const anchor = errorKey(locator, row);
  return (
    // justify-end bottom-aligns the input so a two-line label doesn't push it
    // out of line with neighboring cells.
    <label
      className='flex flex-col justify-end gap-1 text-xs text-muted-foreground'
      data-field-anchor={anchor}
    >
      {label}
      <Input
        inputSize='sm'
        value={value ?? ''}
        disabled={disabled}
        // A warning is advisory, not invalid input — reserve aria-invalid
        // (and the red outline) for findings that block Create Claim.
        aria-invalid={error && severity !== 'warning' ? true : undefined}
        aria-describedby={error ? `${anchor}-error` : undefined}
        className={error ? worksheetFindingInputClass(severity) : undefined}
        onChange={event => onChange(event.target.value)}
      />
      <WorksheetFieldError
        id={`${anchor}-error`}
        error={error}
        severity={severity}
      />
    </label>
  );
}

export function PayerInsuredSection({
  value,
  onChange,
  disabled = false,
  errors,
}: {
  value: UB04PayerRow[];
  onChange: (next: UB04PayerRow[]) => void;
  disabled?: boolean;
  /**
   * Save-time findings keyed by locator + payer row. Optional so the
   * NOA form, which shares this section's sibling grid components, is
   * unaffected.
   */
  errors?: WorksheetErrorMap;
}) {
  const visibleRowCount = Math.max(
    1,
    ...value.map(payer => ROWS.indexOf(payer.row) + 1)
  );
  const rows = ROWS.slice(0, visibleRowCount).map(
    row => value.find(payer => payer.row === row) ?? emptyRow(row)
  );

  const update = (row: 'A' | 'B' | 'C', patch: Partial<UB04PayerRow>) => {
    const next = rows.map(payer =>
      payer.row === row ? { ...payer, ...patch, row } : payer
    );
    onChange(next);
  };

  const addRow = () => {
    const row = ROWS[rows.length];
    if (row) {
      onChange([...rows, emptyRow(row)]);
    }
  };

  return (
    <section className='space-y-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm ring-1 ring-slate-100/70'>
      <div>
        <h3 className='flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-600'>
          <span className='h-1.5 w-1.5 rounded-full bg-teal-500' />
          FL 50-65 Payers and insured
        </h3>
        <p className='mt-1 text-xs text-muted-foreground'>Rows A, B, and C.</p>
      </div>
      <div className='space-y-3'>
        {rows.map(row => (
          <div
            key={row.row}
            className='grid gap-2 rounded-lg border border-slate-200 bg-slate-50/70 p-3 md:grid-cols-3 xl:grid-cols-6'
          >
            <div className='md:col-span-3 xl:col-span-6'>
              <span className='inline-flex items-center rounded-md bg-slate-200/80 px-2 py-0.5 text-xs font-semibold text-slate-700'>
                Row {row.row}
              </span>
            </div>
            <PayerField
              label='FL 50 Payer name'
              locator='50'
              row={row.row}
              value={row.payer_name}
              errors={errors}
              disabled={disabled}
              onChange={payer_name => update(row.row, { payer_name })}
            />
            <PayerField
              label='FL 51 Health plan ID'
              locator='51'
              row={row.row}
              value={row.health_plan_id}
              errors={errors}
              disabled={disabled}
              onChange={health_plan_id => update(row.row, { health_plan_id })}
            />
            <PayerField
              label='FL 52 Release of information'
              locator='52'
              row={row.row}
              value={row.release_of_information}
              errors={errors}
              disabled={disabled}
              onChange={release_of_information =>
                update(row.row, { release_of_information })
              }
            />
            <PayerField
              label='FL 53 Assignment of benefits'
              locator='53'
              row={row.row}
              value={row.assignment_of_benefits}
              errors={errors}
              disabled={disabled}
              onChange={assignment_of_benefits =>
                update(row.row, { assignment_of_benefits })
              }
            />
            <PayerField
              label='FL 57 Other provider ID'
              locator='57'
              row={row.row}
              value={row.other_provider_id}
              errors={errors}
              disabled={disabled}
              onChange={other_provider_id =>
                update(row.row, { other_provider_id })
              }
            />
            <PayerField
              label='FL 58 Insured last name'
              locator='58'
              row={row.row}
              value={row.insured_last_name}
              errors={errors}
              disabled={disabled}
              onChange={insured_last_name =>
                update(row.row, { insured_last_name })
              }
            />
            <PayerField
              label='FL 58 Insured first name'
              locator='58'
              row={row.row}
              value={row.insured_first_name}
              errors={errors}
              disabled={disabled}
              onChange={insured_first_name =>
                update(row.row, { insured_first_name })
              }
            />
            <PayerField
              label='FL 58 Insured middle name'
              locator='58'
              row={row.row}
              value={row.insured_middle_name}
              errors={errors}
              disabled={disabled}
              onChange={insured_middle_name =>
                update(row.row, { insured_middle_name })
              }
            />
            <PayerField
              label='FL 58 Insured suffix'
              locator='58'
              row={row.row}
              value={row.insured_suffix}
              errors={errors}
              disabled={disabled}
              onChange={insured_suffix => update(row.row, { insured_suffix })}
            />
            <label className='flex flex-col justify-end gap-1 text-xs text-muted-foreground'>
              FL 59 Patient relationship
              <Select
                value={row.patient_relationship ?? ''}
                disabled={disabled}
                onValueChange={patient_relationship =>
                  update(row.row, { patient_relationship })
                }
              >
                <SelectTrigger
                  selectSize='sm'
                  aria-label='FL 59 Patient relationship'
                >
                  <SelectValue placeholder='Select relationship' />
                </SelectTrigger>
                <SelectContent>
                  {PATIENT_RELATIONSHIP_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
            <PayerField
              label='FL 60 Insured ID'
              locator='60'
              row={row.row}
              value={row.insured_id}
              errors={errors}
              disabled={disabled}
              onChange={insured_id => update(row.row, { insured_id })}
            />
            <PayerField
              label='FL 61 Group name'
              locator='61'
              row={row.row}
              value={row.group_name}
              errors={errors}
              disabled={disabled}
              onChange={group_name => update(row.row, { group_name })}
            />
            <PayerField
              label='FL 62 Group number'
              locator='62'
              row={row.row}
              value={row.group_number}
              errors={errors}
              disabled={disabled}
              onChange={group_number => update(row.row, { group_number })}
            />
            <PayerField
              label='FL 63 Authorization code'
              locator='63'
              row={row.row}
              value={row.authorization_code}
              errors={errors}
              disabled={disabled}
              onChange={authorization_code =>
                update(row.row, { authorization_code })
              }
            />
            <PayerField
              label='FL 65 Employer name'
              locator='65'
              row={row.row}
              value={row.employer_name}
              errors={errors}
              disabled={disabled}
              onChange={employer_name => update(row.row, { employer_name })}
            />
          </div>
        ))}
      </div>
      {rows.length < ROWS.length && (
        <Button
          type='button'
          size='sm'
          variant='outline'
          disabled={disabled}
          onClick={addRow}
        >
          Add payer row
        </Button>
      )}
    </section>
  );
}
